/**
 * The cash book capture grid's rules — everything the screen decides that is
 * not a rectangle on the page.
 *
 * IN `lib/` AND NOT IN CashBook.jsx, FOR THE REASON views.js STATES AT LENGTH:
 * `npm test -w web` is node's own runner over `src/lib/*.test.js`, and node
 * cannot parse JSX. A rule that lives in a component is a rule this repository
 * cannot run — and a capture grid is nothing but rules about what to send, when
 * a row is ready, and which row a refusal belongs to.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE DOES MONEY ARITHMETIC, AND THAT IS NOT AN OVERSIGHT.
 * ---------------------------------------------------------------------------
 *
 * api.js's header states the rule from the client's side and
 * routes/cashBook.js states it from the server's: the wire carries a decimal
 * string to show and a `*_cents` string to compare, and the browser adds
 * nothing up. So:
 *
 * - The running total, the batch total and every subtotal come from `totals` in
 *   the API's own response to the write that changed them. There is no adding
 *   of a column anywhere in this module or in the screen that uses it.
 * - The exclusive/inclusive pair is NEVER derived here. The bookkeeper types
 *   the side the statement shows, `lineBody` sends exactly the side (or sides)
 *   they typed, and lib/cashBook.js derives the other under the line's own tax
 *   code at the rate effective on the line's own date. Decision 4 exists
 *   because the figure typed must be the figure stored, and a helpful
 *   `× 1.15` here would put back the cent the MULTIRISK row proves is real.
 * - `amountProblem` is a SHAPE check and deliberately mirrors `toCents`'s
 *   grammar rather than tightening it: it exists to catch a fat-fingered "12..3"
 *   before a round trip, and anything it lets through is still the server's to
 *   refuse.
 * - `balanceSide` reads a MINUS SIGN off a cents string. That is a string test,
 *   not a sum, and it is what lets the status bar print Pastel's `D`/`C`.
 */

/** Pastel's two tabs. The batch carries the direction, so a line has no sign. */
export const DIRECTIONS = [
  ['payment', 'Payments'],
  ['receipt', 'Receipts'],
];

/**
 * Pastel's `GCS` column, and the letter it shows.
 *
 * Decision 3: a line names a GL account, a customer or a supplier, and which
 * one it names decides whether it posts straight to the ledger or through
 * `postPayment` into a subledger.
 */
export const CONTRA_KINDS = [
  ['ledger', 'G', 'General ledger account'],
  ['customer', 'C', 'Customer'],
  ['supplier', 'S', 'Supplier'],
];

export const gcs = (contraKind) =>
  CONTRA_KINDS.find(([k]) => k === contraKind)?.[1] ?? '?';

/** A row the bookkeeper is typing. Every field is a string, as typed. */
export function blankRow({
  transactionDate = '', contraKind = 'ledger', taxCodeId = '', reference = '',
} = {}) {
  return {
    id: null,
    lineNo: null,
    transactionDate,
    contraKind,
    contraAccountId: '',
    counterpartyId: '',
    reference,
    description: '',
    taxCodeId,
    amountExcl: '',
    amountIncl: '',
  };
}

/** A saved line, as the row that edits it. */
export function rowFromLine(line) {
  return {
    id: line.id,
    lineNo: line.lineNo,
    transactionDate: line.transactionDate ?? '',
    contraKind: line.contraKind,
    contraAccountId: line.contraAccountId ?? '',
    counterpartyId: line.counterpartyId ?? '',
    reference: line.reference ?? '',
    description: line.description ?? '',
    taxCodeId: line.taxCodeId ?? '',
    // BOTH sides, exactly as stored. The screen shows the pair the bookkeeper
    // typed; re-deriving either one here is the cent decision 4 protects.
    amountExcl: line.amountExcl ?? '',
    amountIncl: line.amountIncl ?? '',
  };
}

/**
 * Has this draft row been touched at all? An untouched row is never sent.
 *
 * THE REFERENCE IS NOT PART OF THIS TEST, for exactly the reason the date is
 * not: `nextReference` puts one on every draft row before anybody has typed a
 * character, so a reference is no longer evidence that somebody did. Were it
 * counted, the row the grid hands the bookkeeper after every save would open
 * already "touched" — and `rowProblem` would answer it with *"a line needs a
 * description"* in red under an empty field they have not reached yet.
 *
 * Nothing is lost: a row with a reference and nothing else cannot be saved
 * anyway, because `rowProblem` demands a description, an account and an amount.
 */
export function isRowEmpty(row) {
  return !row.contraAccountId && !row.counterpartyId
    && !String(row.description).trim()
    && !String(row.amountExcl).trim() && !String(row.amountIncl).trim();
}

// ------------------------------------------------------- the reference -----

/**
 * How long a reference may be on this screen.
 *
 * `cash_batch_line.reference` is `text` and refuses nothing; this is the
 * screen's own limit, and it lives here rather than as a literal in the JSX so
 * that the input's `maxLength` and the increment below cannot drift apart.
 */
export const REFERENCE_MAX = 64;

/**
 * The next reference down the batch: `P27-0206` → `P27-0207`.
 *
 * Finding 2 of the design spec, and the accountant's own request: the reference
 * column of the owner's real batch runs `P27-0206`, `P27-0207` … `P27-0228`,
 * one per line, and typing twenty-three of those by hand is twenty-three
 * chances to fat-finger.
 *
 * ⚠ THIS IS NOT `next_counter` AND MUST NEVER BECOME IT. The batch number is
 * gapless, per entity, allocated at post time under a row lock, and deliberately
 * not guessable. This is a *typing aid* on a free-text column: not gapless, not
 * unique, not authoritative, and fully editable — which is the whole point. The
 * caller feeds it the reference the bookkeeper is looking at, so an override
 * typed over row 4 is what row 5 counts on from; the seed is never remembered.
 *
 * The rules, and why each one is what it is:
 *
 * - **A blank stays blank.** A batch captured with no references at all is
 *   ordinary — `reference` is nullable and `lineBody` sends `null` for an empty
 *   one — and answering that with `1` would start a numbering scheme nobody
 *   asked for and put junk on every line of it.
 * - **No trailing digits: carried unchanged.** `EFT` stays `EFT`. There is no
 *   number to increment, and inventing `EFT1` invents the scheme again. Copying
 *   it forward is still the ergonomic win the request asked for, and one keypress
 *   changes it.
 * - **The trailing run of digits is what moves, and its width is preserved.**
 *   `P27-0009` → `P27-0010`, never `P27-10`: the zeros are how the reference
 *   sorts and how it matches the bank's own numbering. Only the LAST run moves —
 *   `INV 12 of 34` → `INV 12 of 35` — because it is the counter and the rest is
 *   the name of the thing being counted.
 * - **A run that needs another digit gets one.** `P27-0999` → `P27-1000`.
 *   Padding is a floor, never a ceiling.
 * - **BigInt, not Number.** A 64-character reference can be 64 digits, and
 *   `Number` stops counting accurately at sixteen of them. This is the one place
 *   in this module that does arithmetic, and it is arithmetic on a label rather
 *   than on money — the header's rule is about cents and is untouched.
 * - **A reference that would outgrow the column is carried unchanged.** Only an
 *   all-nines run at exactly the limit can do it. Returning the seed shows the
 *   bookkeeper a reference that is visibly not new, which they can fix; a
 *   truncated one would be a different, wrong number that looks right.
 */
export function nextReference(reference) {
  const seed = String(reference ?? '').trim();
  if (!seed) return '';
  const m = /^([\s\S]*?)(\d+)$/.exec(seed);
  if (!m) return seed;
  const [, head, digits] = m;
  const next = (BigInt(digits) + 1n).toString().padStart(digits.length, '0');
  const out = `${head}${next}`;
  return out.length > REFERENCE_MAX ? seed : out;
}

/**
 * What the draft row opens on when a batch is loaded: the next reference after
 * the last line already captured.
 *
 * Re-opening a batch half-captured yesterday should carry on where it stopped,
 * not make somebody read the last row and retype its reference plus one. The
 * last line is the one with the highest `line_no` rather than the last of the
 * array, so this does not quietly depend on the order the API happened to send.
 */
export function nextReferenceAfter(lines = []) {
  const last = (lines ?? []).reduce(
    (best, l) => (best === null || (l.lineNo ?? 0) >= (best.lineNo ?? 0) ? l : best), null);
  return nextReference(last?.reference);
}

/**
 * Does this look like an amount?
 *
 * The same grammar `toCents` accepts — a leading R, spaces and non-breaking
 * spaces, a comma as either the decimal separator or a thousands separator, at
 * most two decimals. Deliberately no stricter: this is here to save a round
 * trip on an obvious mis-key, not to become a second rule about what an amount
 * is. Returns a sentence, or null.
 */
export function amountProblem(text) {
  const raw = String(text ?? '').trim();
  if (raw === '') return null;
  const s = raw.replace(/[Rr\s ]/g, '').replace(/,(\d{1,2})$/, '.$1').replace(/,/g, '');
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return `"${raw}" is not an amount`;
  if (m[1] === '-') {
    // The direction lives on the batch — the tab you are typing in IS the sign.
    return 'a cash book line is never negative: the tab you are on carries the sign';
  }
  if ((m[3] ?? '').length > 2) {
    return `${raw} has more than 2 decimal places; round it deliberately first`;
  }
  return null;
}

// ------------------------------------------------------- the proposal ------

/**
 * What to ask `GET /cash-batches/:id/proposal` about the row being typed — a
 * query string, or null when there is nothing worth asking.
 *
 * A BANK LINE HAS NO SUPPLIER COLUMN. What a statement prints in place of one
 * is the DESCRIPTION, and the reference beside it is the second half of the
 * matcher's keyword haystack — lib/cashBook.js's `proposeAllocation` says so at
 * length and this is the same rename on this side of the wire.
 *
 * LEDGER ROWS ONLY, which is `autoAllocated`'s own first test and not a second
 * rule: an allocation rule names a general ledger account and has no
 * counterparty column at all, so a customer or supplier row is not one the
 * engine has anything to say about.
 *
 * THE STRING IS ALSO THE IDENTITY OF THE ANSWER. `proposalFor` below compares
 * it against the one a held proposal was fetched for, so a proposal for
 * "SPAR" can never be read as the answer for "SPAR TYGERVALLEY" while the next
 * request is still in flight.
 *
 * An amount that is not yet an amount is simply left out rather than sent for
 * the server to refuse: a bookkeeper half-way through typing "1" of "1000.00"
 * is not a mis-key, and a 400 per keystroke is not a suggestion.
 */
export function proposalQuery(row) {
  const description = String(row?.description ?? '').trim();
  if (!description || row.contraKind !== 'ledger') return null;

  const params = new URLSearchParams({ description });
  const reference = String(row.reference ?? '').trim();
  if (reference) params.set('reference', reference);
  const date = String(row.transactionDate ?? '').trim();
  if (date) params.set('date', date);

  // A GROSS FIGURE IS WHAT A BOUNDED RULE IS WRITTEN AGAINST, so the inclusive
  // side is preferred and the exclusive one is the fallback — the same order
  // the library and the route both state, kept in step here rather than guessed.
  const incl = String(row.amountIncl ?? '').trim();
  const excl = String(row.amountExcl ?? '').trim();
  if (incl && !amountProblem(incl)) params.set('amountIncl', incl);
  else if (excl && !amountProblem(excl)) params.set('amountExcl', excl);

  return params.toString();
}

/**
 * The held proposal, but ONLY if it is still the answer to this row.
 *
 * `held` is `{ query, proposal }` — whatever was last fetched, and what it was
 * fetched for. The moment the row moves on, the answer is stale and this
 * returns null, so a rule matched on a description that has since been edited
 * cannot code the line that is actually sent.
 */
export const proposalFor = (row, held) =>
  (held && held.query && held.query === proposalQuery(row) ? held.proposal : null);

/**
 * Will this row be coded by a rule when it is saved?
 *
 * ⚠ `autoApply` IS READ, NEVER RE-DECIDED. It is true for a confirmed rule and
 * for nothing else; a history proposal is SHOWN and waits to be accepted, which
 * is `allocationRules.js:9-11` and the reason `acceptProposal` below exists at
 * all. The server asks the same question again on the POST, so this is a
 * screen's expectation and never the authority.
 *
 * AN ACCOUNT THE BOOKKEEPER TYPED IS THEIR ANSWER, and a rule does not get a
 * vote on it — the library refuses to overrule one and there is no reason to
 * ask it to.
 */
export const autoAllocates = (row, proposal) =>
  Boolean(proposal?.autoApply) && row?.contraKind === 'ledger' && !row.contraAccountId;

/**
 * Accepting a suggestion — the patch that puts it on the row.
 *
 * THE HUMAN HALF OF THE GATE. A history proposal reaches a line only through
 * this function, and this function is only ever called from a button somebody
 * pressed. It takes BOTH fields the proposal names: an explicit accept means
 * the whole suggestion, including a VAT code that differs from the one carried
 * over from the row above.
 */
export function acceptProposal(row, proposal) {
  if (!proposal?.accountId) return {};
  return { contraAccountId: proposal.accountId, taxCodeId: proposal.taxCodeId ?? '' };
}

/**
 * Is this row ready to send? A sentence, or null.
 *
 * These are the library's own refusals said early, while the bookkeeper is
 * still looking at the field — not a second set of rules. Every one of them is
 * refused again by lib/cashBook.js, which is where they are load-bearing.
 *
 * `proposal` IS WHY THE ACCOUNT CHECK IS CONDITIONAL. A row whose account a
 * confirmed RULE is about to supply is not a row missing an account, and
 * refusing it here would put back exactly the wall this task was opened to take
 * down. A history proposal changes nothing: `autoAllocates` is false for one,
 * and the row still needs an account the bookkeeper chose.
 */
export function rowProblem(row, proposal = null) {
  if (!String(row.description ?? '').trim()) {
    return 'a line needs a description: it is the narration the general ledger will carry';
  }
  if (row.contraKind === 'ledger') {
    if (!row.contraAccountId && !autoAllocates(row, proposal)) {
      return 'a ledger line names the account it posts to';
    }
  } else if (!row.counterpartyId) {
    return `a ${row.contraKind} line names the ${row.contraKind} it settles with`;
  }
  const excl = String(row.amountExcl ?? '').trim();
  const incl = String(row.amountIncl ?? '').trim();
  if (!excl && !incl) return 'type the exclusive or the inclusive amount — the other is derived';
  return amountProblem(excl) ?? amountProblem(incl);
}

/**
 * The body for POST .../lines.
 *
 * ONLY THE SIDE THE BOOKKEEPER TYPED TRAVELS. Sending a blank as "0.00" would
 * make every row an AMOUNTS_DISAGREE; sending a derived figure would make the
 * browser the author of a number it is not allowed to compute. A blank key is
 * absent, which is what `resolveLine` reads as "derive this one".
 *
 * The route's schema is `.strict()`, so a key it does not know is a 400 rather
 * than a silent drop — which means this function may never invent one.
 *
 * ---------------------------------------------------------------------------
 * `autoAllocate` AND THE TWO KEYS IT HAS TO LEAVE OUT.
 * ---------------------------------------------------------------------------
 *
 * `autoAllocated` in lib/cashBook.js fills in ONLY WHAT THE BOOKKEEPER LEFT
 * BLANK, and it reads "left blank" as the key being ABSENT — an explicit
 * `null` is a deliberate answer ("no account", "no VAT code") and a rule does
 * not overrule one. So asking for auto-allocation while still sending
 * `contraAccountId: null` would be asking a question and vetoing the answer in
 * the same request, and the rule would silently never fire. The key is omitted
 * instead, and the tax code with it — but only when it is blank, because a VAT
 * code the bookkeeper actually chose still wins over the rule's.
 */
export function lineBody(row, { autoAllocate = false } = {}) {
  const isLedger = row.contraKind === 'ledger';
  const trimmed = (v) => String(v ?? '').trim();
  const body = {
    transactionDate: trimmed(row.transactionDate),
    contraKind: row.contraKind,
    description: trimmed(row.description),
    reference: trimmed(row.reference) || null,
  };
  if (isLedger && autoAllocate) {
    body.autoAllocate = true;
    if (row.taxCodeId) body.taxCodeId = row.taxCodeId;
  } else if (isLedger) {
    body.contraAccountId = row.contraAccountId || null;
    // An empty select is "no VAT code", which is a real answer — ten of the
    // twenty-three rows of the owner's own reference batch carry code 00.
    body.taxCodeId = row.taxCodeId || null;
  } else {
    body.counterpartyId = row.counterpartyId || null;
  }
  if (trimmed(row.amountExcl)) body.amountExcl = trimmed(row.amountExcl);
  if (trimmed(row.amountIncl)) body.amountIncl = trimmed(row.amountIncl);
  return body;
}

/**
 * The body for PATCH .../lines/:id — ONLY what actually changed.
 *
 * Two reasons it is a difference rather than the whole row. A PATCH that
 * re-sends both amounts unchanged is fine, but a PATCH that re-sends both after
 * the bookkeeper corrected ONE of them is an AMOUNTS_DISAGREE with nothing
 * wrong: `resolveLine` re-derives the other side only when exactly one side was
 * given. And an edit that changes nothing should cost no round trip at all,
 * which `{}` (empty) tells the caller.
 *
 * Changing the kind carries its identifying column with it, changed or not —
 * a customer line that used to be a ledger line needs its counterparty in the
 * same request that renames the kind.
 */
export function patchBody(row, line) {
  const was = rowFromLine(line);
  const patch = {};
  const trimmed = (v) => String(v ?? '').trim();

  if (trimmed(row.transactionDate) !== trimmed(was.transactionDate)) {
    patch.transactionDate = trimmed(row.transactionDate);
  }
  if (trimmed(row.description) !== trimmed(was.description)) {
    patch.description = trimmed(row.description);
  }
  if (trimmed(row.reference) !== trimmed(was.reference)) {
    patch.reference = trimmed(row.reference) || null;
  }

  const kindChanged = row.contraKind !== was.contraKind;
  if (kindChanged) patch.contraKind = row.contraKind;
  if (row.contraKind === 'ledger') {
    if (kindChanged || row.contraAccountId !== was.contraAccountId) {
      patch.contraAccountId = row.contraAccountId || null;
    }
    if (kindChanged || (row.taxCodeId || '') !== (was.taxCodeId || '')) {
      patch.taxCodeId = row.taxCodeId || null;
    }
  } else if (kindChanged || row.counterpartyId !== was.counterpartyId) {
    patch.counterpartyId = row.counterpartyId || null;
  }

  // Compared as typed. "27.12" and "27.120" are the same money and different
  // text; the server would answer the same line either way, so the only cost of
  // treating them as a change is one request that changes nothing.
  if (trimmed(row.amountExcl) !== trimmed(was.amountExcl)) {
    patch.amountExcl = trimmed(row.amountExcl);
  }
  if (trimmed(row.amountIncl) !== trimmed(was.amountIncl)) {
    patch.amountIncl = trimmed(row.amountIncl);
  }
  return patch;
}

/**
 * `totals.unpostable`, indexed by the row it belongs to.
 *
 * The list travels per line precisely so the screen can point at the row that
 * is wrong; a banner listing seven reasons with no row to attach them to is the
 * thing the API went out of its way not to send.
 */
export function unpostableByLine(unpostable = []) {
  const byLine = new Map();
  for (const u of unpostable) {
    if (u.lineNo == null) continue;
    if (!byLine.has(u.lineNo)) byLine.set(u.lineNo, []);
    byLine.get(u.lineNo).push(u);
  }
  return byLine;
}

/** The refusals that belong to the batch rather than to any row of it. */
export const batchLevelReasons = (unpostable = []) => unpostable.filter((u) => u.lineNo == null);

/** Empty is the postable case — the same sentence the API's Post button uses. */
export const isPostable = (totals) => Boolean(totals) && (totals.unpostable ?? []).length === 0;

/**
 * Which side of the account a balance sits on, Pastel's `D`/`C`.
 *
 * A minus sign on a cents string, and nothing else. Positive is a debit in this
 * ledger, so `-9427284` is a credit balance and prints `C`. Zero has no side.
 */
export function balanceSide(cents) {
  const s = String(cents ?? '').trim();
  if (s === '' || /^-?0+$/.test(s)) return '';
  return s.startsWith('-') ? 'C' : 'D';
}

/**
 * One account's balance, off a trial balance the screen already fetched.
 *
 * The two balances Pastel shows — the cash book's own in the header and the
 * contra account's in the status bar — are the SAME question asked about two
 * accounts, so they are answered from one report rather than two endpoints.
 * `closing` is formatted by the server and `closing_cents` is the string this
 * reads a sign off; neither is added to anything.
 *
 * `unsigned` IS THE FIGURE TO PRINT, with `side` beside it: a balance is shown
 * the way a ledger shows one — `94 272.84 C`, not `(94 272.84) C`, which says
 * the same thing twice and reads like a negative credit. `amount` keeps the
 * signed string for anything that would rather have it. Stripping a leading
 * minus is a string operation; `money()` already does exactly that to decide
 * where to put its brackets.
 */
export function accountBalance(rows, accountId) {
  if (!accountId) return null;
  const r = (rows ?? []).find((x) => x.account_id === accountId);
  if (!r) return null;
  return {
    accountId,
    code: r.account_code,
    name: r.account_name,
    amount: r.closing,
    unsigned: String(r.closing ?? '').replace(/^-/, ''),
    side: balanceSide(r.closing_cents),
  };
}

/** The accounts a cash book may be opened against — decision 9's declared flag. */
export const bankAccounts = (accounts = []) =>
  accounts.filter((a) => a.is_bank && a.is_active);

/**
 * The accounts a line may post to.
 *
 * The batch's OWN bank account is left out, and only that one: a line to
 * another bank account is an ordinary transfer — row 10 of the owner's
 * reference batch is exactly that — while a line to this batch's own cash book
 * nets to zero and is the SELF_BANK_CONTRA the library refuses.
 */
export const contraOptions = (accounts = [], bankAccountId = null) =>
  accounts.filter((a) => a.is_postable && a.is_active && a.id !== bankAccountId);

/** Customers or suppliers, for a `C` or `S` row. */
export const counterpartyOptions = (counterparties = [], contraKind) =>
  counterparties.filter((c) => c.type === contraKind && c.is_active);

/**
 * VAT codes, the batch's own direction first.
 *
 * A payments batch buys and claims input tax; a receipts batch sells and
 * declares output tax (spec §3, cases A and C). The other direction is listed
 * rather than hidden, because a refund is a real thing and this is a picker,
 * not a rule — the rules are `resolveLine`'s.
 */
export function taxCodeOptions(taxCodes = [], direction) {
  const wanted = direction === 'receipt' ? 'output' : 'input';
  const mine = taxCodes.filter((t) => t.direction === wanted);
  const rest = taxCodes.filter((t) => t.direction !== wanted);
  return [...mine, ...rest];
}

/** The period a date falls in — Pastel's `Per` column, before the line is saved. */
export const periodForDate = (periods = [], date) =>
  (date ? periods.find((p) => p.start_date <= date && date <= p.end_date) ?? null : null);

/** The period a saved line was captured into. */
export const periodById = (periods = [], periodId) =>
  (periodId ? periods.find((p) => p.id === periodId) ?? null : null);

/** Pastel prints the period as its two-digit sequence: `03`. */
export const periodTag = (period) =>
  (period ? String(period.sequence).padStart(2, '0') : '—');

/**
 * Pastel's status bar, left-hand cell: `P27-0206=27.12D`.
 *
 * The reference and the inclusive amount of the row the cursor is on, with the
 * side the CONTRA leg lands on — a payment debits what it bought, a receipt
 * credits what it sold. Nothing is computed: the amount is the formatted string
 * the server sent, and the letter is a property of the tab you are typing in.
 */
export function focusLabel(line, direction) {
  if (!line) return '';
  const ref = String(line.reference ?? '').trim();
  const amount = line.amountIncl ?? '';
  if (!amount) return ref;
  return `${ref ? `${ref}=` : ''}${amount}${direction === 'receipt' ? 'C' : 'D'}`;
}

/**
 * ---------------------------------------------------------------------------
 * THE SEAM FOR `Match <F3>` — the allocation picker, which is NOT built.
 * ---------------------------------------------------------------------------
 *
 * `POST /cash-batches/:batchId/post` takes `allocations`, keyed BY LINE ID:
 * `{ [lineId]: [{ documentId, amount }] }`, handed straight to `postPayment`.
 * No task in the milestone plan owns the picker that fills that map in, so this
 * screen posts with it EMPTY and every customer or supplier line settles ON
 * ACCOUNT — which is the honest default, because the age analysis then reports
 * an unallocated credit rather than silently netting it off the oldest invoice.
 *
 * This function is the whole of what the picker has to feed. A screen that
 * grows one keeps a `{ lineId: [{ documentId, amount }] }` map in state (the
 * open documents come from
 * `GET /entities/:id/documents?counterpartyId=…&unpaidOnly=true`, which
 * Capture.jsx's Payment screen already calls) and passes it here on the way to
 * the post. Nothing else in the post path changes.
 *
 * Lines with no allocation are dropped rather than sent as `[]`, so "on
 * account" travels as the absence of an instruction and not as an empty one.
 */
export function allocationsForPost(map = {}) {
  const out = {};
  for (const [lineId, allocations] of Object.entries(map)) {
    if (Array.isArray(allocations) && allocations.length > 0) out[lineId] = allocations;
  }
  return out;
}

/** Is this a line the Match picker would ever open on? Subledger lines only. */
export const isMatchable = (line) => Boolean(line) && line.contraKind !== 'ledger';

/** What a subledger line will do at post time, given what has been matched. */
export function settlementNote(line, map = {}) {
  if (!isMatchable(line)) return '';
  const allocations = map[line.id];
  if (!Array.isArray(allocations) || allocations.length === 0) return 'on account';
  return `${allocations.length} allocated`;
}

// ------------------------------------------------------------ sorting -----

/**
 * Sorting the grid is DISPLAY ONLY. Nothing here touches a line, its number or
 * a server call; the order the bookkeeper captured in stays the order of
 * `lines`, and `nextReferenceAfter` / the status bar's resting line keep
 * reading that. The click cycle is asc, desc, then back to capture order.
 */
export const SORT_KEYS = [
  'per', 'date', 'gcs', 'account', 'reconciled', 'reference', 'description', 'tax', 'excl', 'incl',
];

/** Next state of the header click cycle: none, asc, desc, none. */
export function nextSort(current, key) {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return null;
}

// Cents, compared as BigInt so a ten-figure amount cannot lose a digit. The
// `*Cents` string is the server's; a bare decimal string is parsed only when
// the cents field is absent.
function centsOf(line, side) {
  const c = line[`${side}Cents`];
  if (c != null && c !== '') { try { return BigInt(c); } catch { /* fall through */ } }
  const t = String(line[side] ?? '').replace(/[R\s ]/g, '').replace(',', '.');
  const m = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(t);
  if (!m || (m[2] === '' && !m[3])) return null;
  const v = BigInt(m[2] || '0') * 100n + BigInt((m[3] ?? '').padEnd(2, '0') || '0');
  return m[1] ? -v : v;
}

const text = (s) => {
  const t = String(s ?? '').trim().toLowerCase();
  return t === '' ? null : t;
};

/** The comparable value of one cell: a string, a bigint, a number, or null (sorts last). */
function sortValue(line, key, { accounts = [], periods = [], taxCodes = [], counterparties = [] }) {
  switch (key) {
    case 'per': {
      const p = periodById(periods, line.periodId) ?? periodForDate(periods, line.transactionDate);
      return p ? p.start_date ?? p.sequence : null;
    }
    case 'date': return line.transactionDate || null;
    case 'gcs': return CONTRA_KINDS.findIndex(([k]) => k === line.contraKind) + 1 || null;
    case 'account': {
      const x = line.contraKind === 'ledger'
        ? accounts.find((a) => a.id === line.contraAccountId)
        : counterparties.find((c) => c.id === line.counterpartyId);
      return x ? [text(x.code) ?? '', text(x.name) ?? ''] : null;
    }
    case 'reconciled': return line.isReconciled ? 1 : 0;
    case 'reference': return text(line.reference);
    case 'description': return text(line.description);
    case 'tax': {
      if (line.contraKind !== 'ledger') return null;   // a subledger line carries no code
      if (!line.taxCodeId) return '00';
      return text(taxCodes.find((t) => t.id === line.taxCodeId)?.code);
    }
    case 'excl': return centsOf(line, 'amountExcl');
    case 'incl': return centsOf(line, 'amountIncl');
    default: return null;
  }
}

function compareValues(a, b) {
  if (Array.isArray(a)) {
    for (let i = 0; i < a.length; i += 1) {
      const c = compareValues(a[i], b[i]);
      if (c !== 0) return c;
    }
    return 0;
  }
  if (typeof a === 'string' && typeof b === 'string') return a < b ? -1 : a > b ? 1 : 0;
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * `lines` in display order for a column and direction ('asc' | 'desc'); any
 * other key or direction returns the capture order (by line number). A new
 * array — the input is not touched. STABLE: ties keep capture order, in both
 * directions. Empty values sort LAST either way, which is why the direction is
 * applied inside the comparison rather than by reversing the result.
 */
export function sortLines(lines = [], ctx = {}, key = null, dir = null) {
  const capture = (a, b) => (a.lineNo ?? 0) - (b.lineNo ?? 0);
  const out = [...lines ?? []];
  if (!SORT_KEYS.includes(key) || (dir !== 'asc' && dir !== 'desc')) return out.sort(capture);
  const sign = dir === 'desc' ? -1 : 1;
  const keyed = out.map((line, i) => ({ line, i, v: sortValue(line, key, ctx) }));
  keyed.sort((x, y) => {
    if (x.v === null && y.v === null) return capture(x.line, y.line) || x.i - y.i;
    if (x.v === null) return 1;
    if (y.v === null) return -1;
    return sign * compareValues(x.v, y.v) || capture(x.line, y.line) || x.i - y.i;
  });
  return keyed.map((k) => k.line);
}

/**
 * The sentence for the answer of POST /cash-batches/:id/reclean-descriptions
 * ({ linesChecked, linesChanged, linesRestored }). Restored lines count as
 * updated: the bookkeeper sees the description change either way.
 */
export function recleanMessage(result) {
  const n = (result?.linesChanged ?? 0) + (result?.linesRestored ?? 0);
  if (n === 0) return 'Nothing to change';
  return `${n} ${n === 1 ? 'description' : 'descriptions'} updated`;
}
